import { Box, Button, Flex, Text } from '@chakra-ui/react'
import { useIntl } from 'react-intl'
import { ChartBarIcon } from '@heroicons/react/24/outline'
import type { Patient } from '../../../../model/patient'
import { getWhoData, type ChartType } from './data'
import {
  interpolatePercentile,
  classifyPoint,
  type DotColor
} from './data/classify'
import { extractPatientPoints } from './GrowthChart'
import messages from './messages'

const CHART_TYPES: ChartType[] = [
  'weightForAge',
  'heightForAge',
  'bmiForAge',
  'headCircumferenceForAge'
]

interface GrowthChartSummaryProps {
  patient: Patient
  visits: any[]
  onOpenCharts: () => void
}

export default function GrowthChartSummary({
  patient,
  visits,
  onOpenCharts
}: GrowthChartSummaryProps) {
  const { formatMessage } = useIntl()

  const counts: Record<DotColor, number> = { green: 0, yellow: 0, red: 0 }
  const sex = patient.sex ?? 'M'

  for (const chartType of CHART_TYPES) {
    const percentiles = getWhoData(chartType, sex)
    const points = extractPatientPoints(visits, chartType, patient.dateOfBirth)
    for (const pt of points) {
      const pct = interpolatePercentile(percentiles, pt.month)
      const color: DotColor = pct ? classifyPoint(pt.value, pct) : 'red'
      counts[color]++
    }
  }

  const total = counts.green + counts.yellow + counts.red
  if (total === 0) return null

  const severity: DotColor =
    counts.red > 0 ? 'red' : counts.yellow > 0 ? 'yellow' : 'green'

  const bannerBg = { red: 'red.50', yellow: 'yellow.50', green: 'green.50' }[
    severity
  ]
  const bannerBorder = {
    red: 'red.200',
    yellow: 'yellow.200',
    green: 'green.200'
  }[severity]
  const titleColor = {
    red: 'red.800',
    yellow: 'yellow.800',
    green: 'green.800'
  }[severity]
  const btnColorScheme = { red: 'red', yellow: 'yellow', green: 'green' }[
    severity
  ]

  return (
    <Flex
      align="center"
      justify="space-between"
      px={{ base: 3, md: 4 }}
      py={{ base: 2, md: 2.5 }}
      mx={{ base: 0, md: 2, '2xl': 4 }}
      mb={3}
      bg={bannerBg}
      border="1px solid"
      borderColor={bannerBorder}
      borderRadius="md"
      gap={{ base: 2, md: 4 }}
      flexWrap="wrap"
    >
      <Flex
        align="center"
        gap={{ base: 2, md: 4 }}
        rowGap={1}
        flexWrap="wrap"
      >
        <Text fontSize="sm" fontWeight="semibold" color={titleColor}>
          {formatMessage(messages.summaryTitle)}:
        </Text>
        {counts.red > 0 && (
          <Flex align="center" gap={1.5}>
            <Box
              w={2.5}
              h={2.5}
              borderRadius="full"
              bg="#ef4444"
              flexShrink={0}
            />
            <Text fontSize="sm" color="gray.700">
              {counts.red} {formatMessage(messages.abnormalRange)}
            </Text>
          </Flex>
        )}
        {counts.yellow > 0 && (
          <Flex align="center" gap={1.5}>
            <Box
              w={2.5}
              h={2.5}
              borderRadius="full"
              bg="#eab308"
              flexShrink={0}
            />
            <Text fontSize="sm" color="gray.700">
              {counts.yellow} {formatMessage(messages.borderlineRange)}
            </Text>
          </Flex>
        )}
        {counts.green > 0 && (
          <Flex align="center" gap={1.5}>
            <Box
              w={2.5}
              h={2.5}
              borderRadius="full"
              bg="#22c55e"
              flexShrink={0}
            />
            <Text fontSize="sm" color="gray.700">
              {counts.green} {formatMessage(messages.normalRange)}
            </Text>
          </Flex>
        )}
      </Flex>
      <Button
        size="xs"
        variant="outline"
        colorScheme={btnColorScheme}
        onClick={onOpenCharts}
        leftIcon={<ChartBarIcon style={{ width: '14px', height: '14px' }} />}
      >
        {formatMessage(messages.viewCharts)}
      </Button>
    </Flex>
  )
}
